import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MapPin, Phone, User } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { photoUrl } from "@/lib/api";
import { ageRangeLabelT } from "@/lib/labels";
import { seekersApi } from "@/lib/endpoints";
import { mapsAvailable, ZoneMap } from "@/components/GoogleMaps";
import { ShareButton } from "@/components/ShareButton";
import { ReportAbuseButton, WithdrawSection } from "@/components/ModerationActions";

export default function SeekerDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activePhoto, setActivePhoto] = useState(0);

  useEffect(() => {
    seekersApi.get(id).then(setS).catch(() => toast.error(t("seekerDetail.notFound")))
      .finally(() => setLoading(false));
  }, [id, t]);

  if (loading) return <p className="text-center text-neutral-500 py-10">{t("common.loading")}</p>;
  if (!s) return <p className="text-center text-neutral-500 py-10">{t("seekerDetail.notFoundDot")}</p>;

  return (
    <div className="flex flex-col gap-4" data-testid="seeker-detail">
      <Link to="/" className="text-sm text-neutral-500 hover:text-[#0066CC] flex items-center gap-1" data-testid="back-link">
        <ArrowLeft className="w-4 h-4" /> {t("common.backToSearch")}
      </Link>

      <div className="bg-white border border-neutral-200 rounded-sm overflow-hidden">
        <div className="aspect-[4/3] bg-neutral-200">
          <img src={photoUrl(s.photo_paths[activePhoto])} alt={s.child_name} className="w-full h-full object-cover" />
        </div>
        {s.photo_paths.length > 1 && (
          <div className="flex gap-2 p-2 overflow-x-auto">
            {s.photo_paths.map((p, i) => (
              <button key={p} onClick={() => setActivePhoto(i)} data-testid={`thumb-${i}`}
                className={`w-14 h-14 shrink-0 rounded-sm overflow-hidden border-2 ${i === activePhoto ? "border-[#0066CC]" : "border-transparent"}`}>
                <img src={photoUrl(p)} alt={`foto ${i + 1}`} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
        <div className="p-4 flex flex-col gap-2">
          <span className="text-[10px] font-bold uppercase tracking-wide bg-[#B45309] text-white px-2 py-0.5 rounded-sm w-fit">{t("card.activeSearch")}</span>
          <h1 className="font-heading text-2xl font-bold tracking-tight" data-testid="seeker-child-name">{s.child_name}</h1>
          <p className="text-neutral-700">{ageRangeLabelT(s.age_range, t)}</p>
          <p className="text-sm text-neutral-500 uppercase tracking-wide flex items-center gap-1"><MapPin className="w-4 h-4" /> {s.municipality}{s.state ? `, ${s.state}` : ""}</p>
          {s.additional_info && <p className="text-sm text-neutral-600">{s.additional_info}</p>}
          {s.status === "localizada" && <span className="text-sm font-semibold text-[#166534]">{t("card.reunited")}</span>}
        </div>
      </div>

      <div className="bg-white border border-neutral-200 rounded-sm p-4 flex flex-col gap-2">
        <p className="text-sm font-semibold text-neutral-900 flex items-center gap-1"><User className="w-4 h-4" /> {t("seekerDetail.reportedBy", { name: s.reporter_name })}</p>
        {s.status === "localizada" ? (
          <p className="text-sm text-[#166534] font-semibold">{t("seekerDetail.reunitedNote")}</p>
        ) : s.phone1 ? (
          <>
            <p className="text-sm text-neutral-600">{t("seekerDetail.ifYouSaw")}</p>
            <a href={`tel:${s.phone1}`} data-testid="seeker-call-1"
              className="bg-[#0066CC] text-white hover:bg-[#0052A3] rounded-sm min-h-[48px] font-semibold flex items-center justify-center gap-2">
              <Phone className="w-5 h-5" /> {t("seekerDetail.call", { phone: s.phone1 })}
            </a>
            {s.phone2 && (
              <a href={`tel:${s.phone2}`} data-testid="seeker-call-2"
                className="bg-white border border-[#0066CC] text-[#0066CC] hover:bg-blue-50 rounded-sm min-h-[48px] font-semibold flex items-center justify-center gap-2">
                <Phone className="w-5 h-5" /> {t("seekerDetail.call", { phone: s.phone2 })}
              </a>
            )}
          </>
        ) : null}
      </div>

      <div className="bg-white border border-neutral-200 rounded-sm p-4 flex flex-col gap-2">
        <p className="text-sm font-semibold text-neutral-900">{t("seekerDetail.shareTitle")}</p>
        <p className="text-xs text-neutral-500">{t("seekerDetail.shareHint")}</p>
        <ShareButton url={`/busqueda/${s.id}`} testid={`share-seeker-${s.id}`}
          text={t("card.shareSeekerText", { name: s.child_name, muni: s.municipality })} />
      </div>

      {mapsAvailable() && (
        <div className="bg-white border border-neutral-200 rounded-sm p-4 flex flex-col gap-2">
          <p className="text-sm font-semibold text-neutral-900">{t("seekerDetail.zoneTitle")}</p>
          <p className="text-xs text-neutral-500">{t("seekerDetail.zoneHint")}</p>
          <ZoneMap municipality={`${s.municipality}, ${s.state || ""}`} />
        </div>
      )}

      <div className="flex items-center justify-between gap-2">
        <ReportAbuseButton testid={`report-seeker-${s.id}`} onReport={(reason) => seekersApi.reportAbuse(s.id, reason)} />
      </div>

      <WithdrawSection testid={`withdraw-seeker-${s.id}`} label={t("seekerDetail.withdrawLabel")}
        onWithdraw={(phone) => seekersApi.withdraw(s.id, phone)} />
    </div>
  );
}
